import React from "react";
import { useGetUsersTransactionsQuery } from "src/features/users-transactions/user-transactions.service";
import { displayDate } from "src/utils/dateHandler.util";
import { Amount } from "src/components/Amount";
import { formatScreamingSnakeCase } from "src/utils/util";
import { transactionStatusStyles, transactionTypeStyles } from "src/features/users-transactions/utils/transaction-style";
import { TransactionLabelPill } from "src/features/users-transactions/components/transactions-table/label";
import { Badge } from "src/features/users-transactions/components/transactions-table/badge";
import PageLoading from "src/components/pageLoading";

type Props = {
  sessionId: string;
};

export const AISessionTransactions: React.FC<Props> = ({ sessionId }) => {
  const { data: transactions, isLoading, isError } = useGetUsersTransactionsQuery({
    where: { sessionId }
  });

  if (isLoading) {
    return <PageLoading />;
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-600">Error loading transactions</p>
      </div>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-500 text-lg">No transactions found for this AI session</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Related Transactions</h3>
        <p className="text-sm text-gray-600 mt-1">
          {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} found
        </p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Transaction ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reason
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transactions.map((transaction) => (
              <tr key={transaction.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 max-w-xs truncate">
                  {transaction.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <Amount amount={transaction.amount} currency={transaction.currency} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <Badge 
                    label={formatScreamingSnakeCase(transaction.type)} 
                    classes={transactionTypeStyles[transaction.type]} 
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <Badge 
                    label={formatScreamingSnakeCase(transaction.status)} 
                    classes={transactionStatusStyles[transaction.status]} 
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {displayDate(transaction.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <TransactionLabelPill label={transaction.label} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
